import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import {
  ArrowRight, Sparkles, Cpu, Shield, Radar, Zap, Cloud,
  Layers, BarChart3, CheckCircle2,
} from 'lucide-react';
import TopBar from '../components/TopBar';
import PageTitle from '../components/PageTitle';
import PortalHero from '../components/PortalHero';
import ProductCard from '../components/ProductCard';
import SectionHeading from '../components/SectionHeading';
import StatsBar from '../components/StatsBar';
import FeatureGrid from '../components/FeatureGrid';
import CTASection from '../components/CTASection';
import SiteFooter from '../components/SiteFooter';

const LLM_URL = import.meta.env.VITE_LLM_URL || 'https://llm.etherworks.cn';
const FINETUNE_URL = import.meta.env.VITE_FINETUNE_URL || 'https://ft.etherworks.cn';

export default function Home() {
  const { t } = useTranslation('landing');
  const { user } = useAuth();

  const primaryCta = user
    ? { label: t('home.open_console'), href: `${LLM_URL}/console`, external: true, icon: <ArrowRight className="h-4 w-4" /> }
    : { label: t('home.start_free'), href: `${LLM_URL}/auth?mode=register`, external: true, icon: <ArrowRight className="h-4 w-4" /> };

  const secondaryCta = { label: t('home.view_pricing'), to: '/plans' };

  const stats = [
    { value: '1B+', label: t('home.stats.samples') },
    { value: '98.6%', label: t('home.stats.accuracy') },
    { value: '<50ms', label: t('home.stats.latency') },
    { value: '744B', label: t('home.stats.params') },
  ];

  const whyItems = [
    { icon: Layers, title: t('home_why.unified_title'), description: t('home_why.unified_desc') },
    { icon: Cpu,    title: t('home_why.frontier_title'), description: t('home_why.frontier_desc') },
    { icon: Zap,    title: t('home_why.speed_title'),    description: t('home_why.speed_desc') },
    { icon: Shield, title: t('home_why.secure_title'),   description: t('home_why.secure_desc') },
  ];

  const benchmarks = [
    { name: 'SWE-bench Verified', ours: '77.8', best: '80.8' },
    { name: 'Terminal-Bench 2.0', ours: '77.3', best: '77.3' },
    { name: 'GPQA-Diamond',       ours: '86.0', best: '94.3' },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <PageTitle titleKey="home.title" namespace="landing" descriptionKey="home.hero_desc" />
      <TopBar />

      {/* Hero */}
      <PortalHero
        accent="emerald"
        badge={<><Sparkles className="h-3.5 w-3.5" />{t('home_portal.badge')}</>}
        title={t('home_portal.title')}
        highlight={t('home_portal.title_highlight')}
        subtitle={t('home_portal.subtitle')}
        description={t('home_portal.description')}
        primary={primaryCta}
        secondary={secondaryCta}
      >
        <StatsBar items={stats} className="max-w-4xl mx-auto" />
      </PortalHero>

      {/* Product Matrix */}
      <section id="products" className="relative py-20 px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow={<><Layers className="h-3.5 w-3.5" />{t('home_portal.matrix_eyebrow')}</>}
          title={t('home_portal.matrix_title')}
          description={t('home_portal.matrix_description')}
          className="mb-12"
        />

        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          <ProductCard
            icon={Cpu}
            accent="emerald"
            name={t('home_card.reverse_model.name')}
            description={t('home_card.reverse_model.desc')}
            to="/products/reverse-model"
            chips={[
              { icon: Shield, label: t('home_card.use_malware'), to: '/products/malware-analysis', color: 'text-blue-500' },
              { icon: Radar,  label: t('home_card.use_vuln'),    to: '/products/vulnerability-mining', color: 'text-indigo-500' },
            ]}
            ctaLabel={t('home_card.explore')}
          />
          <ProductCard
            icon={Zap}
            accent="amber"
            name={t('home_card.finetune.name')}
            description={t('home_card.finetune.desc')}
            href={FINETUNE_URL}
            ctaLabel={t('home_card.launch')}
          />
          <ProductCard
            icon={Cloud}
            accent="cyan"
            name={t('home_card.gpu.name')}
            description={t('home_card.gpu.desc')}
            to="/products/gpu-cloud"
            ctaLabel={t('home_card.explore')}
          />
          <ProductCard
            icon={Shield}
            accent="blue"
            name={t('home_card.malware.name')}
            description={t('home_card.malware.desc')}
            to="/products/malware-analysis"
            ctaLabel={t('home_card.explore')}
          />
          <ProductCard
            icon={Radar}
            accent="indigo"
            name={t('home_card.vuln.name')}
            description={t('home_card.vuln.desc')}
            to="/products/vulnerability-mining"
            ctaLabel={t('home_card.explore')}
          />
        </div>
      </section>

      {/* Why choose us */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 border-t border-border bg-card/20">
        <SectionHeading
          eyebrow={<><CheckCircle2 className="h-3.5 w-3.5" />{t('home_portal.why_eyebrow')}</>}
          title={t('home_portal.why_title')}
          description={t('home_portal.why_description')}
          className="mb-12"
        />
        <div className="max-w-7xl mx-auto">
          <FeatureGrid items={whyItems} columns={4} />
        </div>
      </section>

      {/* Benchmarks (simplified) */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow={<><BarChart3 className="h-3.5 w-3.5" />{t('home_portal.bench_eyebrow')}</>}
          title={t('home_portal.bench_title')}
          description={t('home_portal.bench_description')}
          className="mb-10"
        />
        <div className="max-w-4xl mx-auto rounded-2xl border border-border bg-card overflow-hidden">
          <div className="grid grid-cols-3 px-6 py-3 border-b border-border bg-muted/40 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            <div>{t('home_portal.bench_col_benchmark')}</div>
            <div className="text-right">{t('home_portal.bench_col_ours')}</div>
            <div className="text-right">{t('home_portal.bench_col_best')}</div>
          </div>
          {benchmarks.map((b, i) => {
            const oursWin = parseFloat(b.ours) >= parseFloat(b.best);
            return (
              <div key={i} className="grid grid-cols-3 px-6 py-4 border-b border-border last:border-b-0 items-center">
                <div className="text-sm font-medium text-foreground">{b.name}</div>
                <div className={`text-right font-mono text-sm ${oursWin ? 'text-emerald-500 font-semibold' : 'text-foreground'}`}>{b.ours}</div>
                <div className="text-right font-mono text-sm text-muted-foreground">{b.best}</div>
              </div>
            );
          })}
        </div>
        <div className="mt-6 text-center">
          <a href="/products/reverse-model" className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline">
            {t('home_portal.bench_view_full')}
            <ArrowRight className="h-3.5 w-3.5" />
          </a>
        </div>
      </section>

      {/* CTA */}
      <CTASection
        eyebrow={<><Sparkles className="h-3.5 w-3.5" />{t('home_portal.cta_eyebrow')}</>}
        title={t('home_portal.cta_title')}
        description={t('home_portal.cta_description')}
        primary={user
          ? { label: t('home.open_console'), href: `${LLM_URL}/console`, external: true }
          : { label: t('home.start_free'), href: `${LLM_URL}/auth?mode=register`, external: true }
        }
        secondary={{ label: t('home.view_pricing'), to: '/plans' }}
      />

      <SiteFooter />
    </div>
  );
}
